import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Briefcase, Layers, Settings, LogOut, Menu, X, Zap, Film, Rocket } from "lucide-react";
import { RoleBadge } from "@/components/StatusBadge";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "strategist"] },
  { to: "/assigned-briefs", label: "Assigned Briefs", icon: Briefcase, roles: ["admin", "editor"] },
  { to: "/my-creatives", label: "My Creatives", icon: Film, roles: ["admin", "editor"] },
  { to: "/batch-builder", label: "Creative Batches", icon: Layers, roles: ["admin", "strategist"] },
  { to: "/ready-to-launch", label: "Ready to Launch", icon: Rocket, roles: ["admin", "strategist"] },
  { to: "/settings", label: "Team & Clients", icon: Settings, roles: ["admin", "strategist"] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          onClick={() => setOpen(false)}
          data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
              isActive ? "bg-brand text-ink-900" : "text-zinc-400 hover:text-white hover:bg-ink-800"
            }`
          }
        >
          <it.icon className="w-[18px] h-[18px]" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-ink-900 text-white">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col fixed h-screen border-r border-ink-700 bg-ink-900 p-5">
        <div className="flex items-center gap-2 px-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center">
            <Zap className="w-5 h-5 text-ink-900" fill="#09090B" />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">SuperPumped Studio</span>
        </div>
        <NavItems />
        <div className="mt-auto">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-ink-800 border border-ink-700">
            <div className="w-9 h-9 rounded-full bg-ink-700 overflow-hidden flex items-center justify-center text-sm font-bold">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <RoleBadge role={user?.role} />
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-2 w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-400 hover:text-white hover:bg-ink-800 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-16 bg-ink-900/90 backdrop-blur-xl border-b border-ink-700">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
            <Zap className="w-4 h-4 text-ink-900" fill="#09090B" />
          </div>
          <span className="font-display text-lg font-bold">SuperPumped Studio</span>
        </div>
        <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 top-16 z-40 bg-ink-900 p-5">
          <NavItems />
          <button onClick={logout} className="mt-4 w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-zinc-400 hover:bg-ink-800">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">
        <div className="p-5 sm:p-8 max-w-[1400px] mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
